import type { ReactNode } from 'react';
import { Sparkles, ShieldCheck, KeyRound, Lock, BellRing, Phone, MessageSquare, CheckCircle2, ScanEye, Cpu, HeartPulse } from 'lucide-react';
import type { PlatformModule } from '@/content/platform';

/**
 * Stylised product views drawn in code. They follow the product's own light
 * theme so the site previews what an operator will really see, without
 * depending on screenshots that go stale with every release.
 */

function Window({ title, children, className = '' }: { title: string; children: ReactNode; className?: string }) {
  return (
    <div className={`glass-panel p-3 shadow-xl shadow-navy-900/5 ${className}`}>
      <div className="flex items-center gap-2 mb-3 px-1">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-status-fault/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-status-idle/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-status-running/60" />
        </div>
        <span className="text-[10px] text-text-muted ml-1">{title}</span>
      </div>
      {children}
    </div>
  );
}

const Spark = ({ points, color = '#3b82f6', id }: { points: string; color?: string; id: string }) => (
  <svg viewBox="0 0 200 50" className="w-full h-10" preserveAspectRatio="none">
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity="0.2" />
        <stop offset="100%" stopColor={color} stopOpacity="0" />
      </linearGradient>
    </defs>
    <polygon points={`0,50 ${points} 200,50`} fill={`url(#${id})`} />
    <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export function DashboardMockup() {
  return (
    <Window title="Facility Dashboard" className="max-w-lg mx-auto">
      <div className="grid grid-cols-3 gap-2">
        <div className="glass-card p-3 flex flex-col items-center">
          <div className="w-14 h-14 rounded-full border-4 border-brand-primary/15 relative mb-1.5">
            <div
              className="absolute inset-1 rounded-full"
              style={{
                background: 'conic-gradient(#3b82f6 0% 78%, transparent 78% 100%)',
                mask: 'radial-gradient(farthest-side, transparent 60%, #000 61%)',
                WebkitMask: 'radial-gradient(farthest-side, transparent 60%, #000 61%)',
              }}
            />
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold">78%</span>
          </div>
          <span className="text-[9px] text-text-muted">OEE</span>
        </div>
        <div className="glass-card p-3 flex flex-col items-center justify-center">
          <span className="text-lg font-bold text-brand-primary tabular-nums">1,247</span>
          <span className="text-[9px] text-text-muted">Parts Today</span>
        </div>
        <div className="glass-card p-3 flex flex-col items-center justify-center">
          <span className="text-lg font-bold text-status-running tabular-nums">9/10</span>
          <span className="text-[9px] text-text-muted">Machines Up</span>
        </div>
        <div className="glass-card p-3 col-span-2">
          <span className="text-[9px] text-text-muted block mb-1.5">Throughput</span>
          <Spark id="dash-spark" points="0,40 20,35 40,28 60,32 80,20 100,22 120,15 140,18 160,10 180,12 200,8" />
        </div>
        <div className="glass-card p-3 flex flex-col items-center justify-center">
          <div className="w-3 h-3 rounded-full bg-status-running status-pulse mb-1 text-status-running" />
          <span className="text-[9px] text-text-muted">All Clear</span>
        </div>
        <div className="glass-card p-2.5 col-span-3 space-y-1.5">
          <span className="text-[9px] text-text-muted block">Machine Status</span>
          {[
            { name: 'CNC-01 Haas VF2', ok: true, value: '4,200 RPM' },
            { name: 'CNC-02 Mazak QT', ok: true, value: '3,800 RPM' },
            { name: 'PLC-03 Siemens S7', ok: false, value: 'Waiting' },
            { name: 'Robot-04 FANUC', ok: true, value: 'Cycle 147' },
          ].map((m) => (
            <div key={m.name} className="flex items-center gap-2 text-[9px]">
              <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${m.ok ? 'bg-status-running' : 'bg-status-idle'}`} />
              <span className="text-text-secondary truncate flex-1">{m.name}</span>
              <span className="text-text-muted tabular-nums">{m.value}</span>
            </div>
          ))}
        </div>
      </div>
    </Window>
  );
}

function ProtocolsMockup() {
  const rows = [
    { ip: '192.168.10.21', vendor: 'Allen-Bradley', model: 'CompactLogix 5380', proto: 'EtherNet/IP' },
    { ip: '192.168.10.34', vendor: 'Siemens', model: 'S7-1500', proto: 'S7' },
    { ip: '192.168.10.40', vendor: 'Fanuc', model: '31i-B', proto: 'FOCAS' },
    { ip: '192.168.10.52', vendor: 'Universal Robots', model: 'UR10e', proto: 'UR Dashboard' },
    { ip: '192.168.10.77', vendor: 'Omron', model: 'NX102', proto: 'FINS' },
  ];
  return (
    <Window title="Network Scanner — 192.168.10.0/24" className="max-w-lg mx-auto">
      <div className="flex items-center justify-between px-1 mb-2">
        <span className="text-[10px] font-semibold text-text-primary">5 devices identified</span>
        <span className="text-[9px] px-2 py-0.5 rounded-full bg-status-running/10 text-status-running font-semibold">Scan complete</span>
      </div>
      <div className="glass-card divide-y divide-glass-border">
        {rows.map((r) => (
          <div key={r.ip} className="flex items-center gap-3 px-3 py-2 text-[10px]">
            <Cpu size={14} className="text-brand-primary shrink-0" />
            <span className="font-mono text-text-muted w-24 shrink-0">{r.ip}</span>
            <span className="flex-1 min-w-0 truncate">
              <span className="font-semibold text-text-primary">{r.vendor}</span> <span className="text-text-secondary">{r.model}</span>
            </span>
            <span className="px-1.5 py-0.5 rounded bg-brand-primary/8 text-brand-primary font-medium">{r.proto}</span>
          </div>
        ))}
      </div>
    </Window>
  );
}

function WorkflowMockup() {
  const node = (label: string, color: string, sub: string) => (
    <div className="glass-card overflow-hidden w-32">
      <div className="px-2.5 py-1.5 text-[10px] font-semibold text-white" style={{ backgroundColor: color }}>
        {label}
      </div>
      <div className="px-2.5 py-1.5 text-[9px] text-text-muted">{sub}</div>
    </div>
  );
  return (
    <Window title="Alarm Pipeline — Spindle Overtemp" className="max-w-lg mx-auto">
      <div className="relative rounded-lg bg-ink-muted/70 bg-grid-light p-5 space-y-4">
        <div className="flex items-center justify-between">
          {node('Alarm Active', '#dc2626', 'Spindle temp > 85°C')}
          <div className="flex-1 h-px bg-glass-border-bright mx-2" />
          {node('SMS', '#7c3aed', 'Maintenance group')}
        </div>
        <div className="flex items-center justify-between">
          {node('Delay', '#64748b', 'Wait 10 minutes')}
          <div className="flex-1 h-px bg-glass-border-bright mx-2" />
          {node('Voice Call', '#3b82f6', 'Shift supervisor')}
        </div>
        <div className="glass-card px-3 py-2 flex items-center gap-2 text-[10px]">
          <BellRing size={13} className="text-status-fault" />
          <span className="text-text-secondary flex-1">Escalated to supervisor · voice call placed</span>
          <span className="text-text-muted">02:14</span>
        </div>
      </div>
    </Window>
  );
}

function LogicMockup() {
  // One ladder rung, one SFC sequence, and the two other languages beside them.
  const contact = (label: string, closed = false) => (
    <div className="flex flex-col items-center">
      <span className="text-[8px] font-mono text-text-muted">{label}</span>
      <span className={`px-1 text-[11px] font-mono font-bold ${closed ? 'text-status-running' : 'text-text-secondary'}`}>{closed ? ']/[' : '] ['}</span>
    </div>
  );
  const step = (name: string, active = false) => (
    <div className={`w-20 text-center rounded border-2 px-1.5 py-1 text-[9px] font-semibold ${active ? 'border-status-running bg-status-running/10 text-status-running' : 'border-glass-border-bright bg-white text-text-secondary'}`}>
      {name}
    </div>
  );
  const transition = (label: string) => (
    <div className="flex items-center gap-1.5 h-5">
      <div className="w-px h-full bg-glass-border-bright ml-10" />
      <div className="w-3 h-0.5 bg-text-secondary -ml-[7px]" />
      <span className="text-[8px] font-mono text-text-muted">{label}</span>
    </div>
  );
  return (
    <Window title="Logic — Filler Line" className="max-w-lg mx-auto">
      <div className="grid grid-cols-[1.3fr_1fr] gap-2">
        <div className="space-y-2">
          <div className="glass-card p-2.5">
            <div className="text-[9px] text-text-muted mb-1.5">Ladder · MainRoutine</div>
            <div className="flex items-center">
              <div className="w-0.5 self-stretch bg-status-running" />
              <div className="flex-1 flex items-center gap-1 border-t-2 border-status-running mt-3 pt-0">
                <div className="-mt-3.5 flex items-center gap-1.5 bg-white px-0.5">
                  {contact('Start')}{contact('E_Stop_OK')}{contact('Fault', true)}
                </div>
                <div className="flex-1" />
                <div className="-mt-3.5 flex flex-col items-center bg-white px-0.5">
                  <span className="text-[8px] font-mono text-text-muted">Pump_Run</span>
                  <span className="text-[11px] font-mono font-bold text-status-running">( )</span>
                </div>
              </div>
              <div className="w-0.5 self-stretch bg-text-muted/40" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="glass-card px-2.5 py-2 text-[9px]">
              <div className="font-semibold text-text-primary">Workflow</div>
              <div className="text-text-muted">Shift change → copy count</div>
            </div>
            <div className="glass-card px-2.5 py-2 text-[9px]">
              <div className="font-semibold text-text-primary">Script</div>
              <div className="font-mono text-text-muted truncate">tags.write(…)</div>
            </div>
          </div>
        </div>
        <div className="glass-card p-2.5">
          <div className="text-[9px] text-text-muted mb-1.5">SFC · Batch</div>
          {step('Fill')}
          {transition('Level ≥ 90%')}
          {step('Heat', true)}
          {transition('Temp ≥ 72°C')}
          {step('Drain')}
        </div>
      </div>
    </Window>
  );
}

function AnalyticsMockup() {
  const bars = [
    { label: 'Tool change', v: 92 },
    { label: 'Material', v: 64 },
    { label: 'Setup', v: 41 },
    { label: 'Maintenance', v: 28 },
    { label: 'Quality hold', v: 15 },
  ];
  return (
    <Window title="OEE & Downtime — Line 2, Shift A" className="max-w-lg mx-auto">
      <div className="grid grid-cols-4 gap-2 mb-2">
        {[
          ['OEE', '81%', '#3b82f6'],
          ['Avail.', '89%', '#16a34a'],
          ['Perf.', '93%', '#7c3aed'],
          ['Quality', '98%', '#06b6d4'],
        ].map(([l, v, c]) => (
          <div key={l} className="glass-card p-2 text-center">
            <div className="text-base font-bold tabular-nums" style={{ color: c }}>
              {v}
            </div>
            <div className="text-[9px] text-text-muted">{l}</div>
          </div>
        ))}
      </div>
      <div className="glass-card p-3">
        <div className="text-[9px] text-text-muted mb-2">Downtime Pareto (minutes)</div>
        <div className="space-y-1.5">
          {bars.map((b) => (
            <div key={b.label} className="flex items-center gap-2 text-[9px]">
              <span className="w-20 text-text-secondary truncate">{b.label}</span>
              <div className="flex-1 h-2.5 rounded bg-ink-muted">
                <div className="h-2.5 rounded bg-gradient-to-r from-brand-primary to-brand-secondary" style={{ width: `${b.v}%` }} />
              </div>
              <span className="w-6 text-right tabular-nums text-text-muted">{b.v}</span>
            </div>
          ))}
        </div>
      </div>
    </Window>
  );
}

function MesMockup() {
  return (
    <Window title="Work Order WO-24817" className="max-w-lg mx-auto">
      <div className="grid grid-cols-2 gap-2">
        <div className="glass-card p-3 col-span-2">
          <div className="flex items-center justify-between text-[10px]">
            <span className="font-semibold text-text-primary">Bracket, 6061-T6 · Press 4</span>
            <span className="px-2 py-0.5 rounded-full bg-status-running/10 text-status-running font-semibold">Running</span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-ink-muted">
            <div className="h-2 rounded-full bg-status-running w-[68%]" />
          </div>
          <div className="mt-1.5 flex justify-between text-[9px] text-text-muted">
            <span>1,632 of 2,400 good</span>
            <span>ETA 14:20</span>
          </div>
        </div>
        <div className="glass-card p-3">
          <div className="text-[9px] text-text-muted mb-1.5">Recipe</div>
          <div className="text-[11px] font-semibold">BRKT-6061 rev C</div>
          <div className="mt-1 text-[9px] text-text-secondary">Tonnage 180 · Dwell 0.8 s</div>
        </div>
        <div className="glass-card p-3">
          <div className="text-[9px] text-text-muted mb-1.5">Work instruction</div>
          {['Verify die', 'First-article check', 'Operator sign-off'].map((s, i) => (
            <div key={s} className="flex items-center gap-1.5 text-[9px] text-text-secondary">
              <CheckCircle2 size={10} className={i < 2 ? 'text-status-running' : 'text-text-muted'} /> {s}
            </div>
          ))}
        </div>
        <div className="glass-card p-2.5 col-span-2 flex items-center gap-2 text-[10px]">
          <span className="w-2 h-2 rounded-full bg-status-idle status-pulse text-status-idle" />
          <span className="text-text-secondary flex-1">Andon: Press 4 down — material shortage</span>
          <span className="text-text-muted">3 min</span>
        </div>
      </div>
    </Window>
  );
}

function VisionMockup() {
  return (
    <Window title="Inspection — Cap Station" className="max-w-lg mx-auto">
      <div className="grid grid-cols-[1.4fr_1fr] gap-2">
        <div className="relative aspect-[4/3] rounded-lg bg-slate-800 overflow-hidden">
          <div className="absolute inset-0 bg-grid-dark" />
          {[
            [22, 30],
            [50, 30],
            [78, 30],
            [22, 68],
            [50, 68],
          ].map(([x, y]) => (
            <div key={`${x}-${y}`} className="absolute w-[20%] aspect-square rounded-full border-2 border-status-running bg-slate-300/30 -translate-x-1/2 -translate-y-1/2" style={{ left: `${x}%`, top: `${y}%` }} />
          ))}
          <div className="absolute w-[20%] aspect-square rounded-full border-2 border-status-fault border-dashed -translate-x-1/2 -translate-y-1/2" style={{ left: '78%', top: '68%' }} />
          <div className="absolute left-2 top-2 text-[9px] font-mono text-status-running bg-black/40 px-1.5 rounded">ROI 1</div>
          {/* The calibrated plane's axes, from its origin. */}
          <div className="absolute left-[6%] bottom-[8%] w-[22%] h-0.5 bg-red-400" />
          <div className="absolute left-[6%] bottom-[8%] w-0.5 h-[22%] bg-green-400" />
          <div className="absolute right-2 bottom-2 text-[9px] font-mono text-white bg-black/60 px-1.5 rounded">X 112.40 Y 63.85 mm</div>
        </div>
        <div className="space-y-2">
          <div className="glass-card p-2.5 text-center">
            <div className="text-[9px] text-text-muted">Verdict</div>
            <div className="text-base font-bold text-status-fault">FAIL</div>
          </div>
          {[
            ['tool1/count', '5 / 6'],
            ['tool2/ocv', 'LOT 2417 ✓'],
            ['tool3/dmc', 'Read OK'],
            ['pick/robotXY', '412.6, 238.1'],
          ].map(([k, v]) => (
            <div key={k} className="glass-card px-2.5 py-1.5 flex items-center justify-between text-[9px]">
              <span className="font-mono text-text-muted">{k}</span>
              <span className="font-semibold text-text-primary">{v}</span>
            </div>
          ))}
          <div className="flex items-center gap-1 text-[9px] text-text-muted px-1">
            <ScanEye size={11} /> Image archived
          </div>
        </div>
      </div>
    </Window>
  );
}

function AiMockup() {
  const tags = [
    ['Program:Main.SpindleSpd', 'Speed', '#3b82f6'],
    ['Program:Main.PartCount', 'Count', '#16a34a'],
    ['Program:Main.CoolantTemp', 'Temperature', '#dc2626'],
    ['Program:Main.DoorOpen', 'Interlock', '#ca8a04'],
    ['Program:Main.FaultCode', 'Alarm', '#7c3aed'],
  ];
  return (
    <Window title="Smart Assist — CNC-01" className="max-w-lg mx-auto">
      <div className="glass-card p-3 mb-2 flex items-start gap-2.5">
        <span className="w-7 h-7 rounded-lg bg-brand-secondary/12 text-brand-secondary flex items-center justify-center shrink-0">
          <Sparkles size={15} />
        </span>
        <div className="text-[10px] text-text-secondary leading-relaxed">
          Classified <b className="text-text-primary">214 tags</b>. Suggested 6 alarms and deadbands from 7 days of data. Ready to build a dashboard.
        </div>
      </div>
      <div className="glass-card divide-y divide-glass-border">
        {tags.map(([tag, role, color]) => (
          <div key={tag} className="flex items-center gap-2 px-3 py-1.5 text-[10px]">
            <span className="font-mono text-text-secondary flex-1 truncate">{tag}</span>
            <span className="px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: `${color}14`, color }}>
              {role}
            </span>
          </div>
        ))}
      </div>
    </Window>
  );
}

function SecurityMockup() {
  return (
    <Window title="Security" className="max-w-lg mx-auto">
      <div className="grid grid-cols-2 gap-2">
        {[
          { icon: Lock, title: 'HTTPS', body: 'Certificate from plant CA', ok: true },
          { icon: KeyRound, title: 'MFA', body: 'Required for Admin level', ok: true },
          { icon: ShieldCheck, title: 'Security levels', body: 'Operator · Lead · Engineer · Admin', ok: true },
          { icon: HeartPulse, title: 'Audit', body: '1,284 changes this month', ok: true },
        ].map((c) => (
          <div key={c.title} className="glass-card p-3">
            <c.icon size={15} className="text-brand-primary" />
            <div className="mt-1.5 text-[11px] font-semibold">{c.title}</div>
            <div className="text-[9px] text-text-muted mt-0.5">{c.body}</div>
          </div>
        ))}
        <div className="glass-card p-2.5 col-span-2 text-[9px] font-mono text-text-secondary space-y-1">
          <div>
            <span className="text-text-muted">09:14</span> j.chen changed <b>Press4.Tonnage.HiLimit</b> 185 → 190
          </div>
          <div>
            <span className="text-text-muted">09:02</span> m.ortiz signed in <span className="text-status-running">MFA ✓</span>
          </div>
        </div>
      </div>
    </Window>
  );
}

export function FleetMockup() {
  const rows = [
    { name: 'Plant 1 · Line 2', status: 'online', health: 'Healthy', color: '#16a34a', cpu: 23, temp: 51 },
    { name: 'Plant 1 · Paint', status: 'online', health: 'Disk 87%', color: '#ca8a04', cpu: 41, temp: 58 },
    { name: 'Plant 2 · Molding', status: 'online', health: 'Healthy', color: '#16a34a', cpu: 18, temp: 47 },
    { name: 'Pump Station 7', status: 'offline', health: 'No report 3h', color: '#dc2626', cpu: 0, temp: 0 },
  ];
  return (
    <Window title="Customer Portal — Appliances" className="max-w-lg mx-auto">
      <div className="grid grid-cols-3 gap-2 mb-2">
        {[
          ['Online', '3', '#16a34a'],
          ['Warnings', '1', '#ca8a04'],
          ['Offline', '1', '#dc2626'],
        ].map(([l, v, c]) => (
          <div key={l} className="glass-card p-2 text-center">
            <div className="text-base font-bold" style={{ color: c }}>
              {v}
            </div>
            <div className="text-[9px] text-text-muted">{l}</div>
          </div>
        ))}
      </div>
      <div className="glass-card divide-y divide-glass-border">
        {rows.map((r) => (
          <div key={r.name} className="flex items-center gap-2.5 px-3 py-2 text-[10px]">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: r.color }} />
            <span className="font-semibold text-text-primary flex-1 truncate">{r.name}</span>
            <span className="text-text-muted tabular-nums w-16 text-right">{r.status === 'online' ? `CPU ${r.cpu}%` : '—'}</span>
            <span className="w-20 text-right font-medium" style={{ color: r.color }}>
              {r.health}
            </span>
          </div>
        ))}
      </div>
    </Window>
  );
}

export function AlertsMockup() {
  return (
    <div className="relative max-w-sm mx-auto">
      <div className="rounded-[2rem] border-8 border-navy-900 bg-white shadow-2xl overflow-hidden">
        <div className="bg-ink-muted px-4 py-3 text-center text-[11px] font-semibold text-text-primary border-b border-glass-border">Scada.Ink Alerts</div>
        <div className="p-3 space-y-2.5 min-h-[260px]">
          {[
            ['ALARM Press 4: Hydraulic pressure LOW (1,420 psi).', '02:11'],
            ['ESCALATION Press 4 alarm still active after 10 min. Calling supervisor.', '02:21'],
            ['CLEARED Press 4: Hydraulic pressure normal (1,960 psi).', '02:34'],
          ].map(([m, t]) => (
            <div key={t} className="max-w-[85%] rounded-2xl rounded-tl-sm bg-ink-muted px-3 py-2 text-[11px] text-text-primary leading-snug">
              {m}
              <div className="text-[9px] text-text-muted mt-1">{t}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="absolute -right-4 sm:-right-12 top-24 glass-panel px-3.5 py-2.5 flex items-center gap-2.5 shadow-lg animate-float-slow">
        <span className="w-8 h-8 rounded-full bg-status-running/15 text-status-running flex items-center justify-center">
          <Phone size={15} />
        </span>
        <div>
          <div className="text-[11px] font-bold">Voice call</div>
          <div className="text-[9px] text-text-muted">Supervisor · 0:42</div>
        </div>
      </div>
      <div className="absolute -left-4 sm:-left-12 bottom-10 glass-panel px-3.5 py-2.5 flex items-center gap-2.5 shadow-lg">
        <span className="w-8 h-8 rounded-full bg-brand-secondary/15 text-brand-secondary flex items-center justify-center">
          <MessageSquare size={15} />
        </span>
        <div>
          <div className="text-[11px] font-bold">412 / 500 SMS</div>
          <div className="text-[9px] text-text-muted">This month</div>
        </div>
      </div>
    </div>
  );
}

export function ModuleVisual({ visual }: { visual: PlatformModule['visual'] }) {
  switch (visual) {
    case 'protocols':
      return <ProtocolsMockup />;
    case 'alarms':
      return <WorkflowMockup />;
    case 'logic':
      return <LogicMockup />;
    case 'analytics':
      return <AnalyticsMockup />;
    case 'mes':
      return <MesMockup />;
    case 'vision':
      return <VisionMockup />;
    case 'ai':
      return <AiMockup />;
    case 'security':
      return <SecurityMockup />;
    default:
      return <DashboardMockup />;
  }
}
